import React, { useEffect, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ChevronLeft,
  ChevronRight,
  Layers,
  Pencil,
  Plus,
  Sparkles,
  Trash2,
  Upload,
  X,
  Buildings
} from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import PageHeader from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { FieldError, FieldLabel } from "../components/ui/field";
import { Input } from "../components/ui/input";
import useToast from "../hooks/useToast";
import { uploadFileToS3 } from "../services/uploadService";
import { getDepartments } from "../services/departmentService";
import {
  createCategory,
  deleteCategory,
  getCategories,
  updateCategory
} from "../services/categoryService";

const categorySchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  page_url: z.string().trim().min(1, "Page URL is required").regex(/^[a-z0-9-]+$/, "Must be lowercase, numbers, and hyphens only"),
  image: z.string().trim().min(1, "Sub department image is required"),
  department_id: z.string().min(1, "Department is required"),
  description: z.string().trim().optional()
});

const defaultValues = {
  name: "",
  page_url: "",
  image: "",
  department_id: "",
  description: ""
};

function getVisiblePageNumbers(currentPage, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);
  if (currentPage <= 4) return [1, 2, 3, 4, 5, "...", totalPages];
  if (currentPage >= totalPages - 3) return [1, "...", totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
}

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isDeletingCategory, setIsDeletingCategory] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [subDepartmentPendingDelete, setSubDepartmentPendingDelete] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 1 });

  const fileInputRef = useRef(null);
  const { showErrorToast, showSuccessToast } = useToast();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting }
  } = useForm({
    defaultValues,
    resolver: zodResolver(categorySchema)
  });

  const categoryName = watch("name");
  const categoryImage = watch("image");
  const descriptionContent = watch("description");

  // Auto-slugify name to page_url
  useEffect(() => {
    if (categoryName) {
      const slug = categoryName
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, "")
        .replace(/[\s_-]+/g, "-")
        .replace(/^-+|-+$/g, "");
      setValue("page_url", slug, { shouldValidate: true });
    }
  }, [categoryName, setValue]);

  const refreshListing = (targetPage = pagination.page, targetLimit = pagination.limit) => {
    if (targetPage !== pagination.page || targetLimit !== pagination.limit) {
      setPagination((current) => ({ ...current, page: targetPage, limit: targetLimit }));
      return;
    }
    loadCategories(targetPage, targetLimit);
  };

  async function loadInitialData() {
    const deptRes = await getDepartments({ limit: 100 });
    if (deptRes?.success) setDepartments(deptRes.data.departments || []);
  }

  async function loadCategories(pageArg = pagination.page, limitArg = pagination.limit) {
    setIsLoading(true);
    const response = await getCategories({ page: pageArg, limit: limitArg });
    if (response?.success) {
      setCategories(response.data.categories || []);
      setPagination({
        page: response.data.pagination?.page || pageArg,
        limit: response.data.pagination?.limit || limitArg,
        total: response.data.pagination?.total || 0,
        totalPages: response.data.pagination?.totalPages || 1
      });
    } else {
      showErrorToast(response?.message || "Unable to load categories");
    }
    setIsLoading(false);
  }

  useEffect(() => {
    loadInitialData();
    loadCategories(pagination.page, pagination.limit);
  }, [pagination.page, pagination.limit]);

  const openCreateModal = () => {
    reset(defaultValues);
    setEditingCategoryId(null);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    reset(defaultValues);
    setEditingCategoryId(null);
    setIsModalOpen(false);
  };

  const handleFileUpload = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToast("Please upload an image file");
      return;
    }

    setIsUploadingImage(true);
    uploadFileToS3(file, "categories").then((response) => {
      if (!response?.success) {
        showErrorToast(response?.message || "Unable to upload image");
        setIsUploadingImage(false);
        return;
      }
      setValue("image", response.data.file.url, { shouldDirty: true, shouldValidate: true });
      showSuccessToast("Image uploaded successfully");
      setIsUploadingImage(false);
    });
  };

  const handleEdit = (category) => {
    setEditingCategoryId(category.id);
    reset({
      name: category.name,
      page_url: category.page_url || "",
      image: category.image || "",
      department_id: category.department_id ? String(category.department_id) : "",
      description: category.description || ""
    });
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!subDepartmentPendingDelete) return;
    setIsDeletingCategory(true);
    const response = await deleteCategory(subDepartmentPendingDelete.id);
    setIsDeletingCategory(false);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to delete sub department");
      return;
    }

    const nextPage = categories.length === 1 && pagination.page > 1 ? pagination.page - 1 : pagination.page;
    showSuccessToast(response.message || "Sub department deleted successfully");
    setSubDepartmentPendingDelete(null);
    refreshListing(nextPage, pagination.limit);
  };

  const onSubmit = async (values) => {
    const payload = {
      ...values,
      department_id: values.department_id || null
    };

    const response = editingCategoryId
      ? await updateCategory(editingCategoryId, payload)
      : await createCategory(payload);

    if (!response?.success) {
      showErrorToast(response?.message || "Unable to save sub department");
      return;
    }

    showSuccessToast(response.message || "Sub department saved successfully");
    closeModal();
    if (editingCategoryId) refreshListing(pagination.page, pagination.limit);
    else refreshListing(1, pagination.limit);
  };

  const startItem = pagination.total ? (pagination.page - 1) * pagination.limit + 1 : 0;
  const endItem = pagination.total ? Math.min(pagination.page * pagination.limit, pagination.total) : 0;
  const pageNumbers = getVisiblePageNumbers(pagination.page, pagination.totalPages);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Management"
        title="Sub departments"
        description="Manage service sub departments with departments."
        badge={`${pagination.total} Sub departments`}
      />

      <Card className="border-white/90 bg-white/84">
        <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <CardTitle>Sub department Listing</CardTitle>
            <p className="mt-2 text-sm text-panel-muted">View and manage sub departments.</p>
          </div>
          <Button type="button" onClick={openCreateModal}>
            <Plus className="mr-2 h-4 w-4" /> Add Sub department
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {isLoading ? (
            <div className="rounded-3xl border border-slate-100 bg-slate-50/80 p-6 text-sm text-panel-muted">Loading...</div>
          ) : categories.length ? (
            <>
              <div className="overflow-hidden rounded-3xl border border-slate-100">
                <div className="overflow-x-auto">
                  <table className="min-w-full border-collapse">
                    <thead className="bg-slate-50/90">
                      <tr className="text-left">
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Image</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Name</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted">Department</th>
                        <th className="px-5 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-panel-muted text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white">
                      {categories.map((c) => (
                        <tr key={c.id} className="border-t border-slate-100 align-middle">
                          <td className="px-5 py-4">
                            {c.image ? (
                              <img src={c.image} alt={c.name} className="h-12 w-12 rounded-xl object-cover" />
                            ) : (
                              <div className="h-12 w-12 rounded-xl bg-slate-100 flex items-center justify-center text-slate-400">
                                <Layers size={20} />
                              </div>
                            )}
                          </td>
                          <td className="px-5 py-4 text-sm font-semibold text-panel-ink">{c.name}</td>
                          <td className="px-5 py-4 text-sm text-panel-muted">{c.department_name || "N/A"}</td>
                          <td className="px-5 py-4">
                            <div className="flex justify-end gap-2">
                              <Button type="button" size="sm" variant="outline" onClick={() => handleEdit(c)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button type="button" size="sm" variant="ghost" className="text-rose-500" onClick={() => setSubDepartmentPendingDelete(c)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-3xl border border-slate-100 bg-slate-50/70 px-4 py-4 md:flex-row md:items-center md:justify-between">
                <p className="text-sm text-panel-muted">
                  Showing <span className="font-semibold text-panel-ink">{startItem}</span> to <span className="font-semibold text-panel-ink">{endItem}</span> of <span className="font-semibold text-panel-ink">{pagination.total}</span> categories
                </p>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page <= 1} onClick={() => refreshListing(pagination.page - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  {pageNumbers.map((p, i) => (
                    p === "..." ? <span key={i} className="text-sm">...</span> : 
                    <Button key={i} size="sm" variant={pagination.page === p ? "default" : "outline"} onClick={() => refreshListing(p)}>{p}</Button>
                  ))}
                  <Button type="button" variant="outline" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => refreshListing(pagination.page + 1)}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="rounded-3xl border border-dashed border-slate-200 p-10 text-center">
              <Sparkles className="mx-auto h-10 w-10 text-primary/20 mb-4" />
              <p className="text-lg font-semibold">No Sub department added yet.</p>
              <Button type="button" className="mt-4" onClick={openCreateModal}><Plus className="mr-2" /> Add Your First Sub department</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 overflow-y-auto">
          <div className="max-h-[95dvh] w-full max-w-4xl overflow-y-auto rounded-[32px] border bg-white shadow-2xl p-6">
            <div className="flex items-center justify-between border-b pb-4 mb-6">
              <h2 className="text-2xl font-bold">{editingCategoryId ? "Edit Sub department" : "Add Sub department"}</h2>
              <button type="button" onClick={closeModal}><X /></button>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2 text-panel-ink">
                  <FieldLabel>Sub department Name *</FieldLabel>
                  <Input placeholder="E.g. Cardiology" {...register("name")} />
                  {errors.name && <FieldError>{errors.name.message}</FieldError>}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Page URL *</FieldLabel>
                  <Input placeholder="Enter page-url" {...register("page_url")} />
                  {errors.page_url && <FieldError>{errors.page_url.message}</FieldError>}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Department *</FieldLabel>
                  <select 
                    {...register("department_id")} 
                    className="flex h-11 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select Department</option>
                    {departments.map(d => (
                      <option key={d.id} value={String(d.id)}>{d.name}</option>
                    ))}
                  </select>
                  {errors.department_id && <FieldError>{errors.department_id.message}</FieldError>}
                </div>

                <div className="space-y-2">
                  <FieldLabel>Sub department Image *</FieldLabel>
                  <div className="flex items-center gap-4">
                    {categoryImage && <img src={categoryImage} alt="Preview" className="h-12 w-12 rounded-xl object-cover border" />}
                    <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleFileUpload(e.target.files?.[0])} />
                    <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={isUploadingImage}>
                      <Upload className="h-4 w-4 mr-2" /> {isUploadingImage ? "Uploading..." : "Upload Image"}
                    </Button>
                    {categoryImage && <Button type="button" variant="ghost" size="sm" className="text-rose-500" onClick={() => setValue("image", "")}>Remove</Button>}
                  </div>
                  {errors.image && <FieldError>{errors.image.message}</FieldError>}
                </div>
              </div>

              <div className="space-y-2 category-editor-wrapper">
                <style>{`
                  .category-editor-wrapper .ql-container { height: 200px; overflow-y: auto; }
                  .category-editor-wrapper .ql-editor { min-height: 200px; }
                `}</style>
                <FieldLabel>Description (Rich Text)</FieldLabel>
                <ReactQuill theme="snow" value={descriptionContent || ""} onChange={(c) => setValue("description", c)} />
              </div>

              <div className="flex gap-4 pt-6 border-t font-semibold">
                <Button type="submit" className="px-10" disabled={isSubmitting || isUploadingImage}>
                  {isSubmitting ? "Saving..." : "Save Sub department"}
                </Button>
                <Button type="button" variant="outline" onClick={closeModal}>Cancel</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {subDepartmentPendingDelete && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/50 p-4">
          <div className="w-full max-w-md rounded-[32px] border bg-white p-6 shadow-xl text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-rose-50 text-rose-600 mx-auto mb-4">
              <Trash2 className="h-7 w-7" />
            </div>
            <h2 className="text-2xl font-bold">Delete Sub department?</h2>
            <p className="mt-3 text-panel-muted">Deleting <span className="font-bold text-panel-ink">{subDepartmentPendingDelete.name}</span> is permanent.</p>
            <div className="mt-8 flex gap-4">
              <Button type="button" variant="destructive" className="flex-1" onClick={handleDelete} disabled={isDeletingCategory}>
                {isDeletingCategory ? "Deleting..." : "Confirm Delete"}
              </Button>
              <Button type="button" variant="outline" className="flex-1" onClick={() => setSubDepartmentPendingDelete(null)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
